import React, { useEffect, useMemo } from 'react';

import { fork, hydrate } from 'effector';
import { Provider } from 'effector-react/ssr';

// TODO: Remove after integration
import { faker } from '@faker-js/faker';
import { AppInitialProps } from 'next/app';
import { AppPropsType, NextComponentType, NextPageContext } from 'next/dist/shared/lib/utils';
import Router from 'next/router';
import 'nprogress/nprogress.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-notifications-component/dist/scss/notification.scss';
import SmoothScrollPolyfill from 'smoothscroll-polyfill';

import { $globalData } from '@stores/global-data';
import { rootDomain } from '@stores/root';

import { progressDone, progressStart } from '@utils/nprogress';

import { ErrorBoundary } from '@components/common/ErrorBoundary/ErrorBoundary';
import '@components/common/InvisibleCaptcha/style.scss';

import '../styles/fonts.scss';
import '../styles/global.scss';
import '../styles/vendor/datepicker/datepicker.scss';
import '../styles/vendor/nprogress/nprogress.scss';
import '../styles/vendor/react-notification-manager/react-notification-manager.scss';

// TODO: Remove after integration
faker.seed(0xbad5eed);

type GlobalAppContext = AppPropsType & { ctx: NextPageContext };
type GlobalAppProps = NextComponentType<GlobalAppContext, AppInitialProps, GlobalAppContext>;
const GlobalApp: GlobalAppProps = (props) => {
    const { Component, pageProps } = props;
    const { globalData, ...otherProps } = pageProps;

    const [newScope, key] = useMemo(() => {
        const scope = fork(rootDomain);
        hydrate(scope, {
            values: [[$globalData, globalData]],
        });

        const newKey = Date.now();

        return [scope, newKey];
    }, [globalData]);

    useEffect(() => {
        SmoothScrollPolyfill.polyfill();
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        Router.events.on('routeChangeStart', progressStart);
        Router.events.on('routeChangeComplete', progressDone);
        Router.events.on('routeChangeError', progressDone);

        return () => {
            Router.events.off('routeChangeStart', progressStart);
            Router.events.off('routeChangeComplete', progressDone);
            Router.events.off('routeChangeError', progressDone);
        };
    }, []);

    // Fix scroll to top on route change in Firefox.
    // https://github.com/vercel/next.js/issues/12105
    // https://github.com/vercel/next.js/issues/6462
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const isFirefox = navigator.userAgent.includes('Firefox');
        const htmlElement = document.querySelector('html');

        if (!isFirefox || htmlElement === null) {
            return;
        }

        // Disable smooth scroll on route change start.
        const handleRouteChangeStart = () => {
            htmlElement.style.scrollBehavior = 'auto';
        };

        // Enable smooth scroll back on hash (#anchor) change start.
        const handleHashChangeStart = () => {
            htmlElement.style.scrollBehavior = '';
        };

        Router.events.on('routeChangeStart', handleRouteChangeStart);
        Router.events.on('hashChangeStart', handleHashChangeStart);

        return () => {
            htmlElement.style.scrollBehavior = '';
            Router.events.off('routeChangeStart', handleRouteChangeStart);
            Router.events.off('hashChangeStart', handleHashChangeStart);
        };
    }, []);

    return (
        <ErrorBoundary>
            <Provider value={newScope} key={key}>
                <Component {...otherProps} />
            </Provider>
        </ErrorBoundary>
    );
};

export default GlobalApp;
